import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import {
  PageHead, Card, Pill, StatusPill, FilterBar, TextInput, Select, Loading, Err, tableStyles, EmptyState,
} from "./_shared";

const ENTERPRISE_TYPES = ["agency", "private_label", "enterprise", "reseller", "team"];

export default function Enterprise() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    api.get("/api/admin/workspaces").then((d) => setItems(d.items || [])).catch((e) => setErr(e.message));
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    return items.filter((w) => {
      // primary: enterprise/agency/reseller/private_label/team — fall back to showing
      // any non-personal workspace so the page is useful from day one.
      const enterprisish = ENTERPRISE_TYPES.includes(w.workspace_type) || (w.workspace_type && w.workspace_type !== "personal");
      if (!enterprisish) return false;
      if (type && w.workspace_type !== type) return false;
      if (q && !((w.name || "").toLowerCase().includes(q.toLowerCase()) || (w.id || "").includes(q))) return false;
      return true;
    });
  }, [items, q, type]);

  if (err) return <Err msg={err} />;

  const summary = filtered ? {
    total: filtered.length,
    credits: filtered.reduce((s, w) => s + (w.available_balance || 0), 0),
    jobs: filtered.reduce((s, w) => s + (w.jobs_this_month || 0), 0),
  } : null;

  return (
    <div data-testid="admin-enterprise">
      <PageHead
        title="Enterprise"
        sub={summary ? `${summary.total} enterprise accounts · ${summary.credits.toLocaleString()} credits in float · ${summary.jobs} jobs` : "Loading…"}
        right={<Pill tone="aqua">Enterprise + Agency + Reseller + Private Label</Pill>}
      />

      <FilterBar>
        <TextInput value={q} onChange={setQ} placeholder="Search enterprise accounts…" data-testid="enterprise-search" />
        <Select value={type} onChange={setType} placeholder="All enterprise types"
          options={ENTERPRISE_TYPES} data-testid="enterprise-filter-type" />
      </FilterBar>

      {!filtered ? <Loading /> : filtered.length === 0 ? (
        <EmptyState
          title="No enterprise accounts yet"
          description="Workspaces of type enterprise, agency, reseller, private_label, or team will appear here. Provision one from /admin/workspaces."
        />
      ) : (
        <Card pad={0} style={{ overflow: "hidden" }}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Enterprise</th>
                <th style={tableStyles.th}>Type</th>
                <th style={tableStyles.th}>Status</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Users</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Credits</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Jobs</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Revenue</th>
                <th style={tableStyles.th}>Last active</th>
                <th style={tableStyles.th} />
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} data-testid={`enterprise-row-${w.id}`} style={{ borderTop: "1px solid var(--pl-border)" }}>
                  <td style={tableStyles.td}>
                    <strong>{w.name}</strong>
                    <div style={{ fontSize: 10, color: "var(--pl-text-muted)", fontFamily: "JetBrains Mono, monospace", marginTop: 2 }}>{w.id}</div>
                  </td>
                  <td style={tableStyles.td}><Pill>{w.workspace_type}</Pill></td>
                  <td style={tableStyles.td}><StatusPill s={w.status} /></td>
                  <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{w.member_count ?? 0}</td>
                  <td style={{ ...tableStyles.td, textAlign: "right", fontWeight: 800, color: "var(--pl-aqua)", fontFamily: "JetBrains Mono, monospace" }}>{(w.available_balance ?? 0).toLocaleString()}</td>
                  <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{w.jobs_this_month ?? 0}</td>
                  <td style={{ ...tableStyles.td, textAlign: "right", color: "var(--pl-text-muted)", fontFamily: "JetBrains Mono, monospace" }}>—</td>
                  <td style={{ ...tableStyles.td, fontSize: 11 }}>{(w.updated_at || w.created_at || "").slice(0, 10)}</td>
                  <td style={{ ...tableStyles.td, textAlign: "right", display: "flex", gap: 10, justifyContent: "flex-end" }}>
                    <Link data-testid={`enterprise-open-${w.id}`} to={`/admin/workspaces/${w.id}`}
                      style={linkStyle}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <div style={{ marginTop: 18, padding: 12, borderRadius: 10,
        background: "rgba(39,230,209,0.06)", border: "1px solid rgba(39,230,209,0.22)",
        fontSize: 12, color: "var(--pl-text-muted)", lineHeight: 1.55 }}>
        Revenue figures, child workspaces, and the “Enter Support Mode” action will be wired once Stripe billing (Turn D) and Support Mode runtime (Turn C) ship.
      </div>
    </div>
  );
}

const linkStyle = { color: "var(--pl-aqua)", fontWeight: 800, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" };
